import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError, dao } from '@platform/dao';
import { routes } from '@platform/core/lib/routes';
import { useBulkMutation, useCreateRecordMutation } from '@platform/state/api';
import { useAppDispatch } from '@platform/state/hooks';
import { dialogOpened, selectionCleared, toastShown } from '@platform/state/slices/uiSlice';
import type { IRecordListModel, ISavedViewDefinition } from '@platform/core/types';
import type { IMenuEntry } from '../../menu/IMenuEntry';
import { useAdvancedDsl } from '../advanced/useAdvancedDsl';
import { newRecordPreset } from '../create/newRecordPreset';
import { exportParamsOf, listQueryOf } from '../listQuery';
import { useCsvExport } from '../useCsvExport';
import { copyBody } from './copyBody';
import type { IListActions } from './IListActions';
import { useKnownRecords } from './useKnownRecords';

/**
 * Дії панелі списку (прототип v4): «Вибрати все» — усі записи, що відповідають списку (той самий запит без сторінки), а не лише сторінка;
 * копіювання — нові записи з «(копія)» в назві; видалення — масова дія після підтвердження (`confirm`); експорт — вибрані записи за кодами
 * або весь список (useCsvExport, із заголовками авторизації); масова зміна статусу — діалог BulkDialog. Помилки — спливні повідомлення.
 */
export function useListActions(model: IRecordListModel, entry: IMenuEntry, saved?: ISavedViewDefinition) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const csv = useCsvExport(model, entry, saved);
  const { remember, pick } = useKnownRecords();
  const advanced = useAdvancedDsl(model.ws, model.rt.key);
  const [bulk] = useBulkMutation();
  const [createRecord] = useCreateRecordMutation();
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { ws, rt, module, state, selection, setSelection, page } = model;
  useEffect(() => remember(page?.items), [page, remember]);
  const total = page?.total ?? 0;
  const guard = useCallback(async (job: () => Promise<void>) => {
    setBusy(true);
    try { await job(); } catch (e) {
      dispatch(toastShown({ kind: 'error', text: t('listbar.failed', { message: ApiError.fromUnknown(e).message }) }));
    } finally { setBusy(false); }
  }, [dispatch, t]);
  const clear = useCallback(() => dispatch(selectionCleared(rt.key)), [dispatch, rt.key]);
  const selectAll = () => void guard(async () => {
    const batch = await dao.records.listAll(ws, rt.key, exportParamsOf(listQueryOf(entry, state, rt, saved, advanced)));
    remember(batch.items);
    setSelection(batch.items.map((r) => r.id));
  });
  const copy = () => void guard(async () => {
    const records = pick(selection);
    for (const r of records) await createRecord({ ws, rt: rt.key, body: copyBody(r, t('listbar.copySuffix')) }).unwrap();
    dispatch(toastShown({ kind: 'success', text: t('listbar.copied', { count: records.length }) }));
    clear();
  });
  const confirmDelete = () => guard(async () => {
    const result = await bulk({ ws, rt: rt.key, body: { ids: selection, action: 'delete' } }).unwrap();
    const failed = Object.keys(result.failed).length;
    dispatch(toastShown({ kind: failed ? 'error' : 'success', text: t('listbar.deleted', { count: result.succeeded }) }));
    setDeleting(false);
    clear();
  });
  const exportCsv = () => {
    const codes = pick(selection).map((r) => r.code);
    void csv.run(selection.length && codes.length ? `code:in:${codes.join(',')}` : undefined);
  };
  const allSelected = total > 0 && selection.length >= total;
  const actions: IListActions = {
    create: () => { newRecordPreset.set(null); dispatch(dialogOpened({ kind: 'newRecord', recordType: rt.key })); },
    createWith: (values) => { newRecordPreset.set(values); dispatch(dialogOpened({ kind: 'newRecord', recordType: rt.key })); },
    edit: () => { if (selection[0]) navigate(routes.record(module.module.key, rt.key, selection[0])); },
    copy, remove: () => setDeleting(true), bulkStatus: () => dispatch(dialogOpened({ kind: 'bulk', recordType: rt.key })),
    exportCsv, print: () => window.print(), toggleAll: () => (allSelected ? clear() : selectAll()), selectAll, clear,
    definition: () => navigate(routes.moduleSettings(module.module.key)), allSelected, busy: busy || csv.busy,
  };
  return { actions, confirm: { open: deleting, count: selection.length, cancel: () => setDeleting(false), run: confirmDelete } };
}
